import React from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  FiActivity,
  FiAlertCircle,
  FiArrowUpRight,
  FiCheckCircle,
  FiClock,
  FiFileText,
  FiMessageSquare,
  FiTrendingUp,
  FiUsers,
} from "react-icons/fi";
import { GiScales } from "react-icons/gi";
import styles from "./styles.module.scss";

import { USER_ROLES } from "../../../../enum/UserRoles";
import { AppAPIService } from "../../../../services/AppAPIService";

const extractRows = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.rows)) return response.rows;
  return [];
};

const extractTotal = (response) => {
  const candidates = [
    response?.meta?.total,
    response?.data?.meta?.total,
    response?.total,
    response?.data?.total,
  ];
  const total = candidates.find((value) => Number.isFinite(Number(value)));
  return total === undefined ? extractRows(response).length : Number(total);
};

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value) => {
  const date = parseDate(value);
  return date ? date.toLocaleDateString("pt-BR") : "Data não informada";
};

const relativeDate = (value) => {
  const date = parseDate(value);
  if (!date) return "Data não informada";
  const elapsed = Math.max(0, Date.now() - date.getTime());
  const hours = Math.floor(elapsed / 3600000);
  if (hours < 1) return "Agora há pouco";
  if (hours < 24) return `${hours}h atrás`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "1 dia atrás" : `${days} dias atrás`;
};

const deadlineLabel = (value) => {
  const date = parseDate(value);
  if (!date) return "-";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  const days = Math.round((date.getTime() - today.getTime()) / 86400000);
  if (days === 0) return "Hoje";
  if (days === 1) return "Amanhã";
  if (days < 0) return "Vencido";
  return `${days} dias`;
};

const documentStatusText = {
  completed: "recebido e validado",
  pending: "aguardando envio",
  rejected: "precisa de novo envio",
  validating: "enviado para validação",
};

const DonutChart = ({ segments, total, centerLabel }) => {
  let offset = 0;
  const radius = 42;
  const circumference = 2 * Math.PI * radius;

  return (
    <div className={styles.donutWrap}>
      <svg className={styles.donut} viewBox="0 0 110 110" role="img" aria-label={centerLabel}>
        <circle className={styles.donutTrack} cx="55" cy="55" r={radius} />
        {segments.map((segment) => {
          const fraction = total > 0 ? segment.value / total : 0;
          const length = fraction * circumference;
          const currentOffset = offset;
          offset += length;
          return (
            <circle
              className={styles.donutSegment}
              cx="55"
              cy="55"
              r={radius}
              key={segment.label}
              style={{
                stroke: segment.color,
                strokeDasharray: `${length} ${circumference - length}`,
                strokeDashoffset: -currentOffset,
              }}
            />
          );
        })}
      </svg>
      <div className={styles.donutCenter}>
        <strong>{total.toLocaleString("pt-BR")}</strong>
        <span>{centerLabel}</span>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const api = React.useMemo(() => AppAPIService.getInstance(), []);

  const user = useSelector((state) => state.auth.user);
  const userRole = user?.role;
  const userName = user?.name || user?.username || "usuário";
  const isClient = userRole === USER_ROLES.CLIENT;
  const isAdmin = userRole === USER_ROLES.ADMIN;
  const [clientData, setClientData] = React.useState({
    lawsuits: [],
    documents: [],
    loading: false,
    unavailable: false,
  });
  const [officeData, setOfficeData] = React.useState({
    lawsuits: [],
    documents: [],
    crawler: null,
    totals: {},
    loading: false,
    unavailable: false,
  });

  const quickAccess = [
    {
      index: "01 / 03",
      title: "Documentos",
      description:
        "Solicitações, envios e validação de documentos do seu processo",
      label: "Consultar documentos",
      path: "/admin/acompanhamentoDocumentos",
      Icon: FiFileText,
    },
    {
      index: "02 / 03",
      title: "Processos",
      description: "Acompanhe todos os seus processos jurídicos em andamento",
      label: "Consultar processos",
      path: "/admin/processo",
      Icon: GiScales,
    },
    {
      index: "03 / 03",
      title: "Assistente",
      description: "Atendimentos, conversas e transferências para a equipe",
      label: "Abrir assistente",
      path: "/admin/assistente",
      Icon: FiMessageSquare,
    },
  ];

  React.useEffect(() => {
    if (!isClient) return undefined;
    let mounted = true;

    const loadClientOverview = async () => {
      setClientData((current) => ({ ...current, loading: true }));
      const [lawsuitsResult, documentsResult] = await Promise.allSettled([
        api.makeHttpRequest({
          method: "GET",
          url: "/lawsuit/list-paginated",
          params: { page: 1, perPage: 1000 },
        }),
        api.makeHttpRequest({
          method: "GET",
          url: "/document/list-paginated",
          params: { page: 1, perPage: 1000 },
        }),
      ]);

      if (!mounted) return;
      setClientData({
        lawsuits:
          lawsuitsResult.status === "fulfilled"
            ? extractRows(lawsuitsResult.value)
            : [],
        documents:
          documentsResult.status === "fulfilled"
            ? extractRows(documentsResult.value)
            : [],
        loading: false,
        unavailable:
          lawsuitsResult.status === "rejected" ||
          documentsResult.status === "rejected",
      });
    };

    loadClientOverview();
    return () => {
      mounted = false;
    };
  }, [api, isClient]);

  React.useEffect(() => {
    if (isClient) return undefined;
    let mounted = true;

    const loadOfficeOverview = async () => {
      setOfficeData((current) => ({ ...current, loading: true }));
      const requests = {
        lawsuits: api.makeHttpRequest({
          method: "GET",
          url: "/lawsuit/list-paginated",
          params: { page: 1, perPage: 500, orderBy: "createdAt", orderByDirection: "desc" },
        }),
        documents: api.makeHttpRequest({
          method: "GET",
          url: "/document/list-paginated",
          params: { page: 1, perPage: 500 },
        }),
        clients: api.makeHttpRequest({
          method: "GET",
          url: "/user/list-paginated",
          params: { page: 1, perPage: 1, optionFilter: USER_ROLES.CLIENT },
        }),
        lawsuitCreated: api.makeHttpRequest({
          method: "GET",
          url: "/lawsuit/list-paginated",
          params: { page: 1, perPage: 1, optionFilter: 1 },
        }),
        lawsuitDocuments: api.makeHttpRequest({
          method: "GET",
          url: "/lawsuit/list-paginated",
          params: { page: 1, perPage: 1, optionFilter: 2 },
        }),
        lawsuitValidation: api.makeHttpRequest({
          method: "GET",
          url: "/lawsuit/list-paginated",
          params: { page: 1, perPage: 1, optionFilter: 3 },
        }),
        lawsuitCompleted: api.makeHttpRequest({
          method: "GET",
          url: "/lawsuit/list-paginated",
          params: { page: 1, perPage: 1, optionFilter: 4 },
        }),
        documentPending: api.makeHttpRequest({
          method: "GET",
          url: "/document/list-paginated?status=pending",
          params: { page: 1, perPage: 1 },
        }),
        documentValidating: api.makeHttpRequest({
          method: "GET",
          url: "/document/list-paginated?status=validating",
          params: { page: 1, perPage: 1 },
        }),
        documentRejected: api.makeHttpRequest({
          method: "GET",
          url: "/document/list-paginated?status=rejected",
          params: { page: 1, perPage: 1 },
        }),
        documentCompleted: api.makeHttpRequest({
          method: "GET",
          url: "/document/list-paginated?status=completed",
          params: { page: 1, perPage: 1 },
        }),
      };
      if (isAdmin) requests.crawler = api.makeHttpRequest({ method: "GET", url: "/projudi/overview" });
      const keys = Object.keys(requests);
      const results = await Promise.allSettled(Object.values(requests));
      if (!mounted) return;

      const resolved = keys.reduce((result, key, index) => {
        result[key] = results[index].status === "fulfilled" ? results[index].value : null;
        return result;
      }, {});

      setOfficeData({
        lawsuits: extractRows(resolved.lawsuits),
        documents: extractRows(resolved.documents),
        crawler: resolved.crawler,
        totals: {
          lawsuits: extractTotal(resolved.lawsuits),
          clients: extractTotal(resolved.clients),
          lawsuitCreated: extractTotal(resolved.lawsuitCreated),
          lawsuitDocuments: extractTotal(resolved.lawsuitDocuments),
          lawsuitValidation: extractTotal(resolved.lawsuitValidation),
          lawsuitCompleted: extractTotal(resolved.lawsuitCompleted),
          documentPending: extractTotal(resolved.documentPending),
          documentValidating: extractTotal(resolved.documentValidating),
          documentRejected: extractTotal(resolved.documentRejected),
          documentCompleted: extractTotal(resolved.documentCompleted),
        },
        loading: false,
        unavailable: results.some((result) => result.status === "rejected"),
      });
    };

    loadOfficeOverview();
    return () => {
      mounted = false;
    };
  }, [api, isAdmin, isClient]);

  const clientOverview = React.useMemo(() => {
    const lawsuits = clientData.lawsuits.filter((item) => item?.active !== false);
    const documents = clientData.documents.filter((item) => item?.active !== false);
    const pendingDocuments = documents.filter(
      (item) => String(item?.status || "").toLowerCase() !== "completed"
    );
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const deadlines = lawsuits
      .filter((item) => {
        const deadline = parseDate(item?.limitDate);
        if (!deadline) return false;
        deadline.setHours(23, 59, 59, 999);
        return deadline.getTime() >= today.getTime();
      })
      .sort(
        (first, second) =>
          parseDate(first.limitDate).getTime() - parseDate(second.limitDate).getTime()
      );
    const activities = [
      ...lawsuits.map((item) => ({
        id: `lawsuit-${item.id}`,
        date: item.updatedAt || item.createdAt,
        title: `${item?.action?.action || "Processo"} atualizado`,
        detail: item.adverseParty || item.companyName || "Processo em acompanhamento",
        type: "lawsuit",
      })),
      ...documents.map((item) => ({
        id: `document-${item.id}`,
        date: item.updatedAt || item.createdAt,
        title: `${item.name || item?.documentCategory?.name || "Documento"} ${
          documentStatusText[String(item.status || "").toLowerCase()] || "atualizado"
        }`,
        detail: item?.lawsuit?.adverseParty || "Documento do processo",
        type: "document",
      })),
    ]
      .filter((item) => parseDate(item.date))
      .sort((first, second) => parseDate(second.date) - parseDate(first.date));

    const weekAgo = Date.now() - 7 * 86400000;
    const recentCount = activities.filter(
      (item) => parseDate(item.date).getTime() >= weekAgo
    ).length;

    return {
      activeLawsuits: lawsuits.length,
      pendingDocuments: pendingDocuments.length,
      nextDeadline: deadlines[0] || null,
      recentCount,
      deadlines: deadlines.slice(0, 3),
      activities: activities.slice(0, 5),
    };
  }, [clientData.documents, clientData.lawsuits]);

  const clientMetrics = [
    {
      label: "Processos ativos",
      value: String(clientOverview.activeLawsuits).padStart(2, "0"),
      detail: "Em acompanhamento",
      path: "/admin/processo",
      Icon: GiScales,
    },
    {
      label: "Documentos pendentes",
      value: String(clientOverview.pendingDocuments).padStart(2, "0"),
      detail: "Aguardando providência",
      path: "/admin/acompanhamentoDocumentos",
      Icon: FiFileText,
    },
    {
      label: "Atualizações recentes",
      value: String(clientOverview.recentCount).padStart(2, "0"),
      detail: "Nos últimos 7 dias",
      path: "/admin/processo",
      Icon: FiActivity,
    },
    {
      label: "Próximo prazo",
      value: clientOverview.nextDeadline
        ? deadlineLabel(clientOverview.nextDeadline.limitDate)
        : "—",
      detail:
        clientOverview.nextDeadline?.action?.action || "Nenhum prazo informado",
      path: "/admin/processo",
      Icon: FiClock,
    },
  ];

  const officeOverview = React.useMemo(() => {
    const totals = officeData.totals || {};
    const processSegments = [
      { label: "Criados", value: totals.lawsuitCreated || 0, color: "var(--xv-primary)" },
      { label: "Aguardando documentos", value: totals.lawsuitDocuments || 0, color: "#b97870" },
      { label: "Em validação", value: totals.lawsuitValidation || 0, color: "#d5a078" },
      { label: "Concluídos", value: totals.lawsuitCompleted || 0, color: "#668b78" },
    ];
    const classifiedProcesses = processSegments.reduce((sum, item) => sum + item.value, 0);
    const processRemainder = Math.max(0, (totals.lawsuits || 0) - classifiedProcesses);
    if (processRemainder) {
      processSegments.push({ label: "Outros", value: processRemainder, color: "#706467" });
    }

    const documentSegments = [
      { label: "Pendentes", value: totals.documentPending || 0, color: "var(--xv-primary)" },
      { label: "Em validação", value: totals.documentValidating || 0, color: "#d5a078" },
      { label: "Rejeitados", value: totals.documentRejected || 0, color: "#a85750" },
      { label: "Concluídos", value: totals.documentCompleted || 0, color: "#668b78" },
    ];
    const totalDocuments = documentSegments.reduce((sum, item) => sum + item.value, 0);
    const pendingDocuments =
      (totals.documentPending || 0) +
      (totals.documentValidating || 0) +
      (totals.documentRejected || 0);

    const actionMap = officeData.lawsuits.reduce((result, item) => {
      const label = item?.action?.action || "Não classificado";
      result[label] = (result[label] || 0) + 1;
      return result;
    }, {});
    const topActions = Object.entries(actionMap)
      .map(([label, value]) => ({ label, value }))
      .sort((first, second) => second.value - first.value)
      .slice(0, 5);
    const maxAction = Math.max(1, ...topActions.map((item) => item.value));

    const monthFormatter = new Intl.DateTimeFormat("pt-BR", { month: "short" });
    const now = new Date();
    const months = Array.from({ length: 6 }, (_, index) => {
      const date = new Date(now.getFullYear(), now.getMonth() - (5 - index), 1);
      return {
        key: `${date.getFullYear()}-${date.getMonth()}`,
        label: monthFormatter.format(date).replace(".", ""),
        value: 0,
      };
    });
    officeData.lawsuits.forEach((item) => {
      const date = parseDate(item.createdAt);
      if (!date) return;
      const key = `${date.getFullYear()}-${date.getMonth()}`;
      const month = months.find((entry) => entry.key === key);
      if (month) month.value += 1;
    });
    const maxMonth = Math.max(1, ...months.map((item) => item.value));

    const upcomingDeadlines = officeData.lawsuits
      .filter((item) => {
        const date = parseDate(item.limitDate);
        if (!date) return false;
        date.setHours(23, 59, 59, 999);
        return date.getTime() >= Date.now();
      })
      .sort((first, second) => parseDate(first.limitDate) - parseDate(second.limitDate))
      .slice(0, 4);

    const documentPriority = { rejected: 0, validating: 1, pending: 2, completed: 3 };
    const documentQueue = officeData.documents
      .filter((item) => String(item.status || "").toLowerCase() !== "completed")
      .sort((first, second) => {
        const statusDifference =
          (documentPriority[String(first.status || "").toLowerCase()] ?? 9) -
          (documentPriority[String(second.status || "").toLowerCase()] ?? 9);
        return statusDifference || parseDate(second.updatedAt || second.createdAt) - parseDate(first.updatedAt || first.createdAt);
      })
      .slice(0, 4);

    return {
      totalProcesses: totals.lawsuits || classifiedProcesses,
      totalClients: totals.clients || 0,
      pendingDocuments,
      completionRate:
        (totals.lawsuits || classifiedProcesses) > 0
          ? Math.round(((totals.lawsuitCompleted || 0) / (totals.lawsuits || classifiedProcesses)) * 100)
          : 0,
      processSegments,
      documentSegments,
      totalDocuments,
      topActions,
      maxAction,
      months,
      maxMonth,
      upcomingDeadlines,
      documentQueue,
    };
  }, [officeData.documents, officeData.lawsuits, officeData.totals]);

  const officeMetrics = [
    {
      label: "Processos cadastrados",
      value: officeOverview.totalProcesses.toLocaleString("pt-BR"),
      detail: "Base processual do escritório",
      path: "/admin/processo",
      Icon: GiScales,
    },
    {
      label: "Clientes ativos",
      value: officeOverview.totalClients.toLocaleString("pt-BR"),
      detail: "Acessos cadastrados",
      path: "/admin/usuario",
      Icon: FiUsers,
    },
    {
      label: "Pendências documentais",
      value: officeOverview.pendingDocuments.toLocaleString("pt-BR"),
      detail: "Pendentes, rejeitados ou em validação",
      path: "/admin/acompanhamentoDocumentos",
      Icon: FiAlertCircle,
    },
    {
      label: "Taxa de conclusão",
      value: `${officeOverview.completionRate}%`,
      detail: "Processos classificados como concluídos",
      path: "/admin/processo",
      Icon: FiTrendingUp,
    },
  ];
  const crawlerDaily = officeData.crawler?.daily || [];
  const crawlerWeek = crawlerDaily.slice(-7);
  const crawlerToday = crawlerDaily.slice(-1)[0] || { leads: 0, sentToCrm: 0 };
  const crawlerTotals = (items) => items.reduce((totals, item) => ({
    leads: totals.leads + Number(item.leads || 0),
    sentToCrm: totals.sentToCrm + Number(item.sentToCrm || 0),
  }), { leads: 0, sentToCrm: 0 });
  const crawlerWeekTotals = crawlerTotals(crawlerWeek);
  const crawlerMonthTotals = crawlerTotals(crawlerDaily);
  const crawlerBestDay = crawlerDaily.reduce((best, item) => Number(item.leads || 0) > Number(best?.leads || 0) ? item : best, null);
  const crawlerActiveDays = crawlerDaily.filter((item) => Number(item.leads || 0) > 0).length;
  const crawlerCrmRate = crawlerMonthTotals.leads
    ? Math.round((crawlerMonthTotals.sentToCrm / crawlerMonthTotals.leads) * 100)
    : 0;

  return (
    <div className={styles.container}>
      <header className={styles.hero}>
        <div>
          <span>Visão geral&nbsp;&nbsp;/&nbsp;&nbsp;Atualizado hoje</span>
          <h1>Olá, {userName}.</h1>
          <p>
            Acompanhe processos, documentos e comunicações com o escritório em
            um único lugar.
          </p>
        </div>
        <Link className={styles.heroLink} to="/admin/assistente">
          Falar com o escritório <FiArrowUpRight />
        </Link>
      </header>
      {isClient && (
        <section className={styles.clientOverview} aria-label="Resumo da conta">
          <div className={styles.metricsGrid}>
            {clientMetrics.map(({ label, value, detail, path, Icon }) => (
              <Link className={styles.metricCard} to={path} key={label}>
                <div className={styles.metricHeading}>
                  <span>{label}</span>
                  <Icon aria-hidden="true" />
                </div>
                <strong>{clientData.loading ? "—" : value}</strong>
                <small>{clientData.loading ? "Atualizando informações" : detail}</small>
              </Link>
            ))}
          </div>

          {clientData.unavailable && (
            <p className={styles.dataNotice}>
              Algumas informações não puderam ser atualizadas agora. Os acessos do portal continuam disponíveis.
            </p>
          )}
        </section>
      )}
      <section className={styles.quickAccess}>
        <div className={styles.sectionHeading}>
          <div>
            <span>Acesso rápido</span>
            <h2>O que você precisa consultar?</h2>
          </div>
          <small>03 áreas disponíveis</small>
        </div>
        <div className={styles.quickGrid}>
          {quickAccess.map(({ index, title, description, label, path, Icon }) => (
            <Link className={styles.quickCard} to={path} key={path}>
              <div className={styles.quickCardTop}>
                <span>{index}</span>
                <div className={styles.quickIcon}><Icon /></div>
              </div>
              <h3>{title}</h3>
              <p>{description}</p>
              <div className={styles.quickCardFooter}>
                <span>{label}</span>
                <FiArrowUpRight />
              </div>
            </Link>
          ))}
        </div>
      </section>
      {isClient ? (
        <section className={styles.clientDetails}>
          <article className={styles.activityCard}>
            <div className={styles.detailHeading}>
              <div>
                <span>Linha do tempo</span>
                <h2>Atividades recentes</h2>
              </div>
              <FiActivity aria-hidden="true" />
            </div>
            <div className={styles.detailList}>
              {clientOverview.activities.length ? (
                clientOverview.activities.map((item) => (
                  <Link to={item.type === "document" ? "/admin/acompanhamentoDocumentos" : "/admin/processo"} key={item.id}>
                    <span className={styles.activityIcon}>
                      {item.type === "document" ? <FiCheckCircle /> : <GiScales />}
                    </span>
                    <span>
                      <strong>{item.title}</strong>
                      <small>{item.detail} · {relativeDate(item.date)}</small>
                    </span>
                    <FiArrowUpRight className={styles.rowArrow} />
                  </Link>
                ))
              ) : (
                <p className={styles.emptyDetail}>Nenhuma atividade recente encontrada.</p>
              )}
            </div>
          </article>

          <article className={styles.deadlineCard}>
            <div className={styles.detailHeading}>
              <div>
                <span>Atenção</span>
                <h2>Próximos prazos</h2>
              </div>
              <FiClock aria-hidden="true" />
            </div>
            <div className={styles.detailList}>
              {clientOverview.deadlines.length ? (
                clientOverview.deadlines.map((item) => (
                  <Link to="/admin/processo" key={item.id}>
                    <span>
                      <strong>{item?.action?.action || "Processo"}</strong>
                      <small>{item.adverseParty || item.companyName || "Processo em acompanhamento"} · Prazo: {formatDate(item.limitDate)}</small>
                    </span>
                    <b>{deadlineLabel(item.limitDate)}</b>
                  </Link>
                ))
              ) : (
                <p className={styles.emptyDetail}>Nenhum prazo informado no momento.</p>
              )}
            </div>
          </article>
        </section>
      ) : (
        <section className={styles.officeDashboard}>
          <div className={styles.sectionHeading}>
            <div>
              <span>Visão operacional</span>
              <h2>Indicadores do escritório</h2>
            </div>
            <small>Dados atualizados ao abrir esta página</small>
          </div>

          <div className={styles.metricsGrid}>
            {officeMetrics.map(({ label, value, detail, path, Icon }) => (
              <Link className={styles.metricCard} to={path} key={label}>
                <div className={styles.metricHeading}>
                  <span>{label}</span>
                  <Icon aria-hidden="true" />
                </div>
                <strong>{officeData.loading ? "—" : value}</strong>
                <small>{officeData.loading ? "Atualizando informações" : detail}</small>
              </Link>
            ))}
          </div>

          {isAdmin && <article className={styles.crawlerPanel}>
            <div className={styles.crawlerHeader}>
              <div><span>Captação automatizada</span><h3>Leads e processamento do crawler</h3><p>Volume diário, aproveitamento no CRM e saúde da operação.</p></div>
              <Link to="/admin/parametrizacao">Ver operação <FiArrowUpRight /></Link>
            </div>
            {officeData.crawler ? <>
              <div className={styles.crawlerMetrics}>
                <div><span>Leads hoje</span><strong>{crawlerToday.leads}</strong><small>{crawlerToday.sentToCrm} enviados ao CRM</small></div>
                <div><span>Últimos 7 dias</span><strong>{crawlerWeekTotals.leads}</strong><small>{crawlerWeekTotals.sentToCrm} enviados ao CRM</small></div>
                <div><span>Últimos 30 dias</span><strong>{crawlerMonthTotals.leads}</strong><small>{crawlerActiveDays} dias com entrada</small></div>
                <div><span>Aproveitamento no CRM</span><strong>{crawlerCrmRate}%</strong><small>{crawlerMonthTotals.sentToCrm} de {crawlerMonthTotals.leads} leads</small></div>
                <div><span>Execuções hoje</span><strong>{officeData.crawler.today?.executions || 0}</strong><small>{officeData.crawler.today?.finished || 0} concluídas · {officeData.crawler.today?.failed || 0} falhas</small></div>
                <div><span>Melhor dia do mês</span><strong>{crawlerBestDay?.leads || 0}</strong><small>{crawlerBestDay ? new Date(`${crawlerBestDay.day}T12:00:00`).toLocaleDateString("pt-BR") : "Sem entradas"}</small></div>
              </div>
              <div className={styles.crawlerCharts}>
                <div>
                  <div className={styles.crawlerChartTitle}><strong>Leads por dia · última semana</strong><small>Total de {crawlerWeekTotals.leads}</small></div>
                  <div className={styles.crawlerChart} aria-label="Leads importados nos últimos sete dias">
                    {crawlerWeek.map(item => {
                      const max = Math.max(1, ...crawlerWeek.map(day => Number(day.leads || 0)));
                      return <div key={item.day} title={`${new Date(`${item.day}T12:00:00`).toLocaleDateString("pt-BR")}: ${item.leads} leads`}><strong>{item.leads}</strong><i><b style={{ height: `${Math.max(4, (Number(item.leads || 0) / max) * 100)}%` }} /></i><span>{new Date(`${item.day}T12:00:00`).toLocaleDateString("pt-BR", { weekday: "short" }).replace(".", "")}</span></div>;
                    })}
                  </div>
                </div>
                <div>
                  <div className={styles.crawlerChartTitle}><strong>Leads por dia · últimos 30 dias</strong><small>Média de {(crawlerMonthTotals.leads / 30).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} por dia</small></div>
                  <div className={`${styles.crawlerChart} ${styles.crawlerMonthChart}`} aria-label="Leads importados nos últimos trinta dias">
                    {crawlerDaily.map((item, index) => {
                      const max = Math.max(1, ...crawlerDaily.map(day => Number(day.leads || 0)));
                      return <div key={item.day} title={`${new Date(`${item.day}T12:00:00`).toLocaleDateString("pt-BR")}: ${item.leads} leads`}><i><b style={{ height: `${Math.max(3, (Number(item.leads || 0) / max) * 100)}%` }} /></i><span>{index % 5 === 0 || index === crawlerDaily.length - 1 ? String(item.day).slice(8, 10) : ""}</span></div>;
                    })}
                  </div>
                </div>
              </div>
              <div className={styles.crawlerFooter}><span>Última execução: <strong>{officeData.crawler.latestExecution ? formatDate(officeData.crawler.latestExecution.updatedAt || officeData.crawler.latestExecution.createdAt) : "—"}</strong></span><span>Status: <strong>{officeData.crawler.latestExecution?.status || "Sem execução registrada"}</strong></span></div>
            </> : <p className={styles.crawlerUnavailable}>Resumo do crawler indisponível no momento. Os demais indicadores continuam atualizados.</p>}
          </article>}

          {officeData.unavailable && (
            <p className={styles.dataNotice}>
              Alguns indicadores não puderam ser atualizados. Os módulos operacionais continuam disponíveis.
            </p>
          )}

          <div className={styles.analyticsGrid}>
            <article className={styles.analyticsCard}>
              <div className={styles.chartHeading}>
                <div>
                  <span>Carteira processual</span>
                  <h3>Processos por status</h3>
                </div>
                <Link to="/admin/processo" aria-label="Abrir processos"><FiArrowUpRight /></Link>
              </div>
              <div className={styles.donutContent}>
                <DonutChart
                  segments={officeOverview.processSegments}
                  total={officeOverview.totalProcesses}
                  centerLabel="processos"
                />
                <div className={styles.chartLegend}>
                  {officeOverview.processSegments.map((item) => (
                    <div key={item.label}>
                      <i style={{ background: item.color }} />
                      <span>{item.label}</span>
                      <strong>{item.value.toLocaleString("pt-BR")}</strong>
                    </div>
                  ))}
                </div>
              </div>
            </article>

            <article className={styles.analyticsCard}>
              <div className={styles.chartHeading}>
                <div>
                  <span>Fluxo documental</span>
                  <h3>Documentos por status</h3>
                </div>
                <Link to="/admin/acompanhamentoDocumentos" aria-label="Abrir documentos"><FiArrowUpRight /></Link>
              </div>
              <div className={styles.donutContent}>
                <DonutChart
                  segments={officeOverview.documentSegments}
                  total={officeOverview.totalDocuments}
                  centerLabel="documentos"
                />
                <div className={styles.chartLegend}>
                  {officeOverview.documentSegments.map((item) => (
                    <div key={item.label}>
                      <i style={{ background: item.color }} />
                      <span>{item.label}</span>
                      <strong>{item.value.toLocaleString("pt-BR")}</strong>
                    </div>
                  ))}
                </div>
              </div>
            </article>

            <article className={`${styles.analyticsCard} ${styles.wideChart}`}>
              <div className={styles.chartHeading}>
                <div>
                  <span>Entrada de processos</span>
                  <h3>Movimento dos últimos 6 meses</h3>
                </div>
                <small>Base: registros recentes carregados</small>
              </div>
              <div className={styles.monthChart}>
                {officeOverview.months.map((item) => (
                  <div className={styles.monthColumn} key={item.key}>
                    <strong>{item.value}</strong>
                    <div>
                      <i style={{ height: `${Math.max(4, (item.value / officeOverview.maxMonth) * 100)}%` }} />
                    </div>
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
            </article>

            <article className={styles.analyticsCard}>
              <div className={styles.chartHeading}>
                <div>
                  <span>Perfil da carteira</span>
                  <h3>Principais naturezas</h3>
                </div>
                <small>Amostra recente</small>
              </div>
              <div className={styles.horizontalBars}>
                {officeOverview.topActions.length ? officeOverview.topActions.map((item) => (
                  <div key={item.label}>
                    <span title={item.label}>{item.label}</span>
                    <div><i style={{ width: `${(item.value / officeOverview.maxAction) * 100}%` }} /></div>
                    <strong>{item.value}</strong>
                  </div>
                )) : <p className={styles.emptyDetail}>Sem dados classificados no momento.</p>}
              </div>
            </article>
          </div>

          <div className={styles.operationsGrid}>
            <article className={styles.activityCard}>
              <div className={styles.detailHeading}>
                <div>
                  <span>Atenção operacional</span>
                  <h2>Próximos prazos</h2>
                </div>
                <FiClock aria-hidden="true" />
              </div>
              <div className={styles.detailList}>
                {officeOverview.upcomingDeadlines.length ? officeOverview.upcomingDeadlines.map((item) => (
                  <Link to="/admin/processo" key={item.id}>
                    <span>
                      <strong>{item?.action?.action || "Processo"}</strong>
                      <small>{item.companyName || item.adverseParty || "Processo cadastrado"} · {formatDate(item.limitDate)}</small>
                    </span>
                    <b>{deadlineLabel(item.limitDate)}</b>
                  </Link>
                )) : <p className={styles.emptyDetail}>Nenhum prazo futuro encontrado na amostra atual.</p>}
              </div>
            </article>

            <article className={styles.deadlineCard}>
              <div className={styles.detailHeading}>
                <div>
                  <span>Fila documental</span>
                  <h2>Itens que pedem atenção</h2>
                </div>
                <FiAlertCircle aria-hidden="true" />
              </div>
              <div className={styles.detailList}>
                {officeOverview.documentQueue.length ? officeOverview.documentQueue.map((item) => (
                  <Link to="/admin/acompanhamentoDocumentos" key={item.id}>
                    <span className={styles.activityIcon}><FiFileText /></span>
                    <span>
                      <strong>{item.name || item?.documentCategory?.name || "Documento"}</strong>
                      <small>{documentStatusText[String(item.status || "").toLowerCase()] || "Requer verificação"} · {item?.user?.name || item?.lawsuit?.companyName || "Cliente não informado"}</small>
                    </span>
                    <FiArrowUpRight className={styles.rowArrow} />
                  </Link>
                )) : <p className={styles.emptyDetail}>Nenhuma pendência documental encontrada.</p>}
              </div>
            </article>
          </div>
        </section>
      )}
    </div>
  );
};
export default Dashboard;
